import { config } from '@brands/config/config';
import Sidenav from '@brands/Dashboard/Sidenav/Sidenav';
import {
  passwordlessRequest,
  PasswordlessRequestResponse,
} from '@brands/services/auth/passwordlessRequest';
import {
  passwordlessVerify,
  PasswordlessVerifyResponse,
} from '@brands/services/auth/passwordlessVerify';
import { formatEmail, formatPhoneNumber } from '@brands/Utils/formatDate';
import { toastError } from '@brands/Utils/toastError';
import React, { FC, SyntheticEvent, useState } from 'react';
import { AiOutlineQuestionCircle } from 'react-icons/ai';
import { toast } from 'react-toastify';

import styles from './styles.module.scss';

interface PasswordlessVerifyProps {
  username: string;
  initialResponse: PasswordlessRequestResponse;
  onCancel: () => void;
  onVerified: (response: PasswordlessVerifyResponse) => Promise<void>;
}

const PasswordlessVerify: FC<PasswordlessVerifyProps> = ({
  username,
  initialResponse,
  onCancel,
  onVerified,
}) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<PasswordlessRequestResponse>(initialResponse);

  const onSubmit = async (event: SyntheticEvent): Promise<void> => {
    event.preventDefault();
    setLoading(true);
    try {
      const verified = await passwordlessVerify({
        username,
        code,
        organization_id: config.organization_id,
        ...(config.division_id && { division_id: config.division_id }),
        ...(config.practice_id && { practice_id: config.practice_id }),
      });
      await onVerified(verified);
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  };

  const onResend = async (): Promise<void> => {
    setLoading(true);
    try {
      const next = await passwordlessRequest({
        username,
        organization_id: config.organization_id,
        ...(config.division_id && { division_id: config.division_id }),
        ...(config.practice_id && { practice_id: config.practice_id }),
      });
      setResponse(next);
      toast.success('We sent you a new code.');
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Sidenav isVisibleOnMobile hideDefaultOptions>
        <a
          href="https://dentistryone.zendesk.com/hc/en-us/articles/11914427760151-How-to-change-your-password"
          target="_blank"
          className={styles.sidenavContainer}
          rel="noreferrer"
          style={{ textDecoration: 'none' }}
        >
          <div className={styles.sidenavToggleText}>Help</div>
          <div className={styles.sidenavToggle}>
            <AiOutlineQuestionCircle />
          </div>
        </a>
      </Sidenav>
      <form className={styles.formContainer} onSubmit={onSubmit}>
        <div className={styles.body}>
          <div className={styles.twoFactorBody}>
            <h1 className="fs-unmask">Check your inbox</h1>
            <div className={styles.verificationCode}>
              {response.mfa_method === 'SMS_MFA' && response.phone_number ? (
                <span className={`${styles.welcomeText} fs-unmask`}>
                  {`We sent a 6-digit code to your phone ending in ${formatPhoneNumber(response.phone_number)}`}
                </span>
              ) : (
                <span className={`${styles.welcomeText} fs-unmask`}>
                  {`We sent a 6-digit code to ${formatEmail(response.email || username)}`}
                </span>
              )}
              <div className={`${styles.inputWrapper} ${styles.inputWrap}`} data-validate="Valid code is: xxxxxx">
                <label htmlFor="otp-code" className="fs-unmask">Enter Code</label>
                <input
                  id="otp-code"
                  className={`${styles.input100} ${styles.profileInputClass} fs-exclude`}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  name="code"
                  placeholder=""
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                />
                <span className={styles.focusInput100} />
              </div>
            </div>
            <div className={styles.resendRow}>
              <span className={styles.resendText}>Didn’t receive the code?</span>
              <button type="button" className={styles.resendBtn} onClick={onResend} disabled={loading}>
                Send a new code
              </button>
            </div>
            <div className={styles.buttonContainer}>
              <div className={styles.buttonWrapper}>
                <div className={styles.buttonBG} />
                <button
                  type="submit"
                  className={`${styles.formBtn} ${styles.signIn} fs-unmask`}
                  disabled={loading || !/^\d{6}$/.test(code)}
                >
                  Continue
                </button>
                <button
                  type="button"
                  className={`${styles.formBtn} ${styles.cancel} fs-unmask`}
                  disabled={loading}
                  onClick={onCancel}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default PasswordlessVerify;
